import type RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import {
  AI_SKILLS,
  AIDriver,
  SimWorld,
  makeInput,
  type AISkillName,
  type VehicleInput,
  type Vehicle,
} from '@polyloop/sim';
import { Renderer } from './renderer.js';
import { TRACKS, DEFAULT_TRACK, type TrackId } from './tracks.js';
import { KeyboardInput } from '../input/keyboard.js';
import { TouchInput } from '../input/touch.js';
import { ChaseCamera } from './camera.js';
import { Hud } from '../hud/hud.js';
import { TrickTracker } from './trick-tracker.js';

export interface GameOpts {
  canvas: HTMLCanvasElement;
  rapier: typeof RAPIER;
  trackId?: TrackId;
  aiCount?: number;
  aiSkill?: AISkillName;
  /** Demo mode: also AI-control the "player" car for screenshot loops. */
  demoMode?: boolean;
}

interface DrivenCar {
  vehicle: Vehicle;
  mesh: THREE.Object3D;
  prevPos: THREE.Vector3;
  prevQuat: THREE.Quaternion;
  nextPos: THREE.Vector3;
  nextQuat: THREE.Quaternion;
  ai: AIDriver | null;
}

const SKILL_PAINTS: Record<AISkillName | 'player', { chassis: number; spoiler: number; cabin: number }> = {
  player:   { chassis: 0xff3ea5, spoiler: 0x00e0ff, cabin: 0x121325 },
  trainee:  { chassis: 0xa8b8c8, spoiler: 0xffffff, cabin: 0x202028 },
  rookie:   { chassis: 0x3a78ff, spoiler: 0x9fc7ff, cabin: 0x1a1f33 },
  ace:      { chassis: 0xff8a3a, spoiler: 0xffe28a, cabin: 0x2a1612 },
  champion: { chassis: 0xffd75c, spoiler: 0xff3ea5, cabin: 0x2a1c00 },
};

export class Game {
  readonly canvas: HTMLCanvasElement;
  readonly rapier: typeof RAPIER;

  readonly renderer: Renderer;
  readonly sim: SimWorld;
  readonly keyboard: KeyboardInput;
  readonly touch: TouchInput;
  readonly camera: ChaseCamera;
  readonly hud: Hud;
  readonly tricks: TrickTracker;

  readonly cars: DrivenCar[] = [];
  private playerCar!: DrivenCar;
  private playerSpawnPos = new THREE.Vector3();
  private playerSpawnQuat = new THREE.Quaternion();

  private input: VehicleInput = makeInput();
  private lastTimeMs = performance.now();
  private running = false;

  private aiCount: number;
  private aiSkill: AISkillName;
  private trackId: TrackId;
  private demoMode: boolean;

  constructor(opts: GameOpts) {
    this.canvas = opts.canvas;
    this.rapier = opts.rapier;
    this.aiCount = Math.max(0, Math.min(3, opts.aiCount ?? 0));
    this.aiSkill = opts.aiSkill ?? 'rookie';
    this.trackId = opts.trackId ?? DEFAULT_TRACK;
    this.demoMode = opts.demoMode ?? false;

    this.renderer = new Renderer(this.canvas);
    this.sim = new SimWorld({ rapier: this.rapier });
    this.keyboard = new KeyboardInput();
    this.touch = new TouchInput();
    this.camera = new ChaseCamera(this.renderer.camera);
    this.hud = new Hud();
    this.tricks = new TrickTracker();
  }

  async start(): Promise<void> {
    const track = TRACKS[this.trackId] ?? TRACKS[DEFAULT_TRACK]!;
    track.build(this.renderer.scene, this.sim);

    if (this.demoMode) {
      this.camera.setCinematic(true);
    }

    // Player car (id 0). In demoMode, also gets an AI driver.
    this.playerCar = this.spawnCar(
      0,
      'player',
      this.demoMode ? this.aiSkill : null,
      track.spawns[0]!,
      this.demoMode ? track.waypoints : undefined,
    );
    this.playerSpawnPos.copy(this.playerCar.prevPos);
    this.playerSpawnQuat.copy(this.playerCar.prevQuat);

    // AI cars (id 1..N).
    for (let i = 0; i < this.aiCount; i++) {
      const spawn = track.spawns[i + 1] ?? track.spawns[0]!;
      this.spawnCar(i + 1, this.aiSkill, this.aiSkill, spawn, track.waypoints);
    }

    this.keyboard.attach();
    this.touch.attach();
    window.addEventListener('resize', () => this.renderer.resize());
    this.renderer.resize();

    this.running = true;
    this.lastTimeMs = performance.now();
    this.loop();
  }

  stop(): void {
    this.running = false;
    this.keyboard.detach();
    this.touch.detach();
  }

  private spawnCar(
    id: number,
    paintKey: AISkillName | 'player',
    aiSkill: AISkillName | null,
    spawn: { position: { x: number; y: number; z: number }; yaw: number },
    waypoints?: readonly { x: number; y: number; z: number }[],
  ): DrivenCar {
    const vehicle = this.sim.addVehicle(id);
    const spawnQuat = quaternionFromYaw(spawn.yaw);
    vehicle.setTransform(spawn.position, spawnQuat);

    const mesh = makeCarMesh(SKILL_PAINTS[paintKey]);
    this.renderer.scene.add(mesh);

    const ai =
      aiSkill !== null && waypoints != null
        ? new AIDriver(
            vehicle,
            waypoints,
            AI_SKILLS[aiSkill]!,
            0x9e3779b1 ^ (id * 0x85ebca77),
          )
        : null;

    const prevPos = new THREE.Vector3(spawn.position.x, spawn.position.y, spawn.position.z);
    const prevQuat = new THREE.Quaternion(spawnQuat.x, spawnQuat.y, spawnQuat.z, spawnQuat.w);
    const car: DrivenCar = {
      vehicle,
      mesh,
      prevPos,
      prevQuat,
      nextPos: prevPos.clone(),
      nextQuat: prevQuat.clone(),
      ai,
    };
    this.cars.push(car);
    return car;
  }

  private loop = (): void => {
    if (!this.running) return;
    requestAnimationFrame(this.loop);

    const nowMs = performance.now();
    // Allow big dt in demo/headless mode so virtual-time-compressed frames
    // can advance the full simulation; normal play stays smooth because real
    // frame intervals are 16ms.
    const dtCap = this.demoMode ? 30 : 0.1;
    const dtSec = Math.min(dtCap, (nowMs - this.lastTimeMs) / 1000);
    this.lastTimeMs = nowMs;

    // Player input. In demoMode, the player car has its own AI driver below,
    // so we skip keyboard/touch read.
    const kbInput = this.keyboard.read(this.input);
    if (!this.demoMode) this.touch.augment(kbInput);

    if (!this.demoMode && kbInput.reset) {
      this.playerCar.vehicle.respawn(
        {
          x: this.playerSpawnPos.x,
          y: this.playerSpawnPos.y,
          z: this.playerSpawnPos.z,
        },
        {
          x: this.playerSpawnQuat.x,
          y: this.playerSpawnQuat.y,
          z: this.playerSpawnQuat.z,
          w: this.playerSpawnQuat.w,
        },
      );
      this.playerCar.prevPos.copy(this.playerSpawnPos);
      this.playerCar.nextPos.copy(this.playerSpawnPos);
      this.playerCar.prevQuat.copy(this.playerSpawnQuat);
      this.playerCar.nextQuat.copy(this.playerSpawnQuat);
      this.tricks.reset();
      kbInput.reset = false;
    }
    if (!this.demoMode) {
      this.sim.setInput(this.playerCar.vehicle.id, kbInput);
    }

    // AI inputs (includes the player car when demoMode is on).
    for (const car of this.cars) {
      if (car.ai) {
        const aiInput = car.ai.step(dtSec);
        this.sim.setInput(car.vehicle.id, aiInput);
      }
    }

    // Capture poses → step → capture new poses.
    for (const car of this.cars) {
      car.prevPos.set(
        car.vehicle.position.x,
        car.vehicle.position.y,
        car.vehicle.position.z,
      );
      car.prevQuat.set(
        car.vehicle.quaternion.x,
        car.vehicle.quaternion.y,
        car.vehicle.quaternion.z,
        car.vehicle.quaternion.w,
      );
    }

    const alpha = this.sim.advance(dtSec);

    for (const car of this.cars) {
      car.nextPos.set(
        car.vehicle.position.x,
        car.vehicle.position.y,
        car.vehicle.position.z,
      );
      car.nextQuat.set(
        car.vehicle.quaternion.x,
        car.vehicle.quaternion.y,
        car.vehicle.quaternion.z,
        car.vehicle.quaternion.w,
      );
      car.mesh.position.lerpVectors(car.prevPos, car.nextPos, alpha);
      car.mesh.quaternion.slerpQuaternions(car.prevQuat, car.nextQuat, alpha);
    }

    // Trick tracking (player only).
    this.tricks.step(this.playerCar.vehicle, dtSec);

    // Camera follows player.
    this.camera.update(
      this.playerCar.mesh.position,
      this.playerCar.mesh.quaternion,
      this.playerCar.vehicle,
      dtSec,
    );

    this.renderer.render();

    this.hud.update({
      speedMs: this.playerCar.vehicle.snapshot().speedMs,
      surface: this.playerCar.vehicle.surface,
      isGrounded: this.playerCar.vehicle.isGrounded,
      airTimeMs: this.playerCar.vehicle.airTimeMs,
      driftPower: this.playerCar.vehicle.driftPower,
      tick: this.sim.tick,
      fps: 1 / Math.max(dtSec, 1e-3),
      score: this.tricks.score,
      events: this.tricks.events,
    });
  };
}

function quaternionFromYaw(yawRad: number): { x: number; y: number; z: number; w: number } {
  const half = yawRad * 0.5;
  return { x: 0, y: Math.sin(half), z: 0, w: Math.cos(half) };
}

function makeCarMesh(paint: { chassis: number; spoiler: number; cabin: number }): THREE.Object3D {
  const group = new THREE.Group();

  const chassisGeo = new THREE.BoxGeometry(1.2, 0.5, 2.4);
  chassisGeo.translate(0, 0.05, 0);
  const chassis = new THREE.Mesh(chassisGeo, new THREE.MeshToonMaterial({ color: paint.chassis }));
  chassis.castShadow = true;
  group.add(chassis);

  const cabinGeo = new THREE.BoxGeometry(0.9, 0.35, 1.2);
  cabinGeo.translate(0, 0.5, -0.1);
  const cabin = new THREE.Mesh(cabinGeo, new THREE.MeshToonMaterial({ color: paint.cabin }));
  cabin.castShadow = true;
  group.add(cabin);

  const spoilerGeo = new THREE.BoxGeometry(1.1, 0.08, 0.15);
  spoilerGeo.translate(0, 0.55, 1.05);
  const spoiler = new THREE.Mesh(
    spoilerGeo,
    new THREE.MeshToonMaterial({ color: paint.spoiler }),
  );
  spoiler.castShadow = true;
  group.add(spoiler);

  const wheelGeo = new THREE.CylinderGeometry(0.32, 0.32, 0.18, 16);
  wheelGeo.rotateZ(Math.PI / 2);
  const wheelMat = new THREE.MeshToonMaterial({ color: 0x101115 });
  for (const [x, y, z] of [
    [0.62, -0.15, 0.85],
    [-0.62, -0.15, 0.85],
    [0.62, -0.15, -0.85],
    [-0.62, -0.15, -0.85],
  ] as const) {
    const w = new THREE.Mesh(wheelGeo, wheelMat);
    w.position.set(x, y, z);
    w.castShadow = true;
    group.add(w);
  }

  // Inverted-hull outline pass (mobile-safe cel outline).
  const outlineGeo = chassisGeo.clone();
  outlineGeo.scale(1.06, 1.06, 1.04);
  const outlineMat = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.BackSide });
  group.add(new THREE.Mesh(outlineGeo, outlineMat));

  return group;
}
